import { FormControl } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import GenericFormField from '../genericFormField/GenericFormField';
import {
  FieldRendererProps,
  GenericFormFieldProps,
} from '../genericFormField/GenericFormField.types';
import TextFieldCharacterCounter from './TextFieldCharacterCounter';

interface Props extends Omit<GenericFormFieldProps, 'fieldRenderer'> {
  maxCharacters?: number;
  trimOnBlur?: boolean;
  maxCharactersDescription?: string;
  onChangeHandler?: (event: React.FormEvent<FormControl>) => void;
}

/**
 * This is a wrapper around the react-bootstrap FormControl component. It should be
 * the standard way to use Input in the app.
 * Any new modifications in the UI (changing design library) should be done here.
 */
const TextField: React.FC<Props> = ({
  name,
  id = name,
  maxCharacters,
  trimOnBlur,
  maxCharactersDescription,
  maxLength,
  componentClass,
  type,
  onChangeHandler,
  ...props
}: Props) => {
  const { setValue } = useFormContext();
  const showCharsCount = !!maxCharacters || !!maxLength;

  const fieldRenderer = ({
    field: { value, ref, onChange, onBlur },
  }: FieldRendererProps) => {
    return (
      <>
        <FormControl
          id={id}
          name={name}
          data-testid={id}
          value={value}
          onChange={(...args) => {
            onChange(...args);
            onChangeHandler?.(...args);
          }}
          onBlur={() => {
            onBlur();
            if (trimOnBlur) setValue(name, value.trim());
          }}
          inputRef={ref}
          maxLength={maxLength}
          componentClass={componentClass}
          type={type}
          {...props}
        />

        {showCharsCount && (
          <TextFieldCharacterCounter
            name={name}
            // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            maxCharacters={maxCharacters}
            maxCharactersDescription={maxCharactersDescription}
            maxLength={maxLength}
          />
        )}
      </>
    );
  };

  const { containerClassName: containerClassNameOrig } = props;
  const containerClassName = showCharsCount
    ? `has-character-counter${
        containerClassNameOrig ? ` ${containerClassNameOrig}` : ''
      }`
    : containerClassNameOrig;

  return (
    <GenericFormField
      name={name}
      fieldRenderer={fieldRenderer}
      containerClassName={containerClassName}
      {...props}
    />
  );
};

TextField.defaultProps = {
  maxCharacters: undefined,
  trimOnBlur: false,
  maxCharactersDescription: undefined,
  onChangeHandler: undefined,
};

export default TextField;
